import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, ChevronLeft, ChevronRight, CircleCheck, TriangleAlert } from 'lucide-react'
import {
  Bidi, Card, CardHeader, Dialog, DialogContent, DialogDescription, DialogTitle,
  EmptyState, ErrorState, Identifier, LoadingState, NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, cellIndex, cellKey, fetchRoster, formatCount, formatDayMonth, formatWeekday,
  localDateFromKey, may, nameIn, setRosterEntry, shiftDateKey, translateError,
  type RosterCell, type RosterPerson, type RosterState, type ShiftOption,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-41 · THE ROSTER. Who is working, and when.

   THIS SCREEN EXISTS BECAUSE OF WHAT HAPPENS WITHOUT IT. The roster was a table and a
   domain rule from the first sprint and had no way in, so on a running server it was
   whatever the seed wrote on the day it was loaded — one day. The second morning of
   testing would have met every attendant with "you are not on today's roster" and every
   supervisor with an empty assignment board, and the cause would have looked like a bug
   in the boards rather than an absence of data.

   A GRID, PEOPLE DOWN AND DAYS ACROSS, and Saturday first because the working week in
   Iraq runs Saturday to Thursday with Friday off. A Monday-based grid puts the rest day
   in the middle of the row, which is exactly where an eye does not expect a gap.

   FOUR STATES AND A FIFTH THING THAT IS NOT A STATE. On shift, rest day, leave, absent —
   and NULL, which means nobody has said yet. The distinction is the point of the screen:
   a week nobody has written looks identical to a week where everybody is off unless the
   two are drawn differently, and the first is a job to do while the second is a decision.

   THE ONE NUMBER THAT IS NOT DECORATION is what somebody is holding. Marking an attendant
   absent does NOT tip their rooms into the pool — deliberately, because "Zahra is absent
   and holding four rooms" is what a supervisor needs at seven in the morning and hiding
   it would hide whose morning just collapsed. So this screen says it, at the moment the
   supervisor makes the choice, rather than leaving them to discover it on another board.
   ========================================================================= */

export function Roster({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const queryClient = useQueryClient()

  /* THE WEEK IS NOT CHOSEN UNTIL SOMEBODY CHOOSES ONE. Undefined means "whatever week
     the hotel is in", which only the server can answer — it needs the property's time
     zone and the rule that the week starts on Saturday, and a browser knows neither.
     Computing a default here is how a supervisor in Najaf gets shown somebody else's
     week on a laptop still set to London. */
  const [from, setFrom] = useState<string | undefined>(undefined)
  const [editing, setEditing] = useState<RosterCell | null>(null)
  const [confirmation, setConfirmation] = useState<string | null>(null)

  const week = useQuery({
    queryKey: ['roster', propertyId, from ?? 'current'],
    queryFn: ({ signal }) => fetchRoster(propertyId, { ...(from ? { from } : {}), signal }),
    retry: false,
  })

  const save = useMutation({
    mutationFn: (body: {
      userId: string; localDate: string; state: RosterState
      shiftId?: string | null; coverage?: string | null
    }) => setRosterEntry(propertyId, body),
    onSuccess: () => {
      setConfirmation(t.rosterSaved)
      setEditing(null)
      void queryClient.invalidateQueries({ queryKey: ['roster', propertyId] })
    },
  })

  const data = week.data
  const cells = useMemo(() => (data ? cellIndex(data) : new Map<string, RosterCell>()), [data])

  /* COUNTED FROM WHAT IS DRAWN. The server does not send a total of unwritten cells and
     must not: a banner saying "12 unwritten" beside a grid showing eleven is the fastest
     way to lose a supervisor's trust in every number on the page. */
  const unwritten = useMemo(
    () => (data ? data.people.length * data.dates.length - data.cells.filter((c) => c.state).length : 0),
    [data])

  if (week.isLoading && !week.error) return <LoadingState rows={6} />

  if (week.error && !data) {
    if (week.error instanceof ApiError && week.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(week.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={week.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  if (!data || data.people.length === 0) return <EmptyState title={t.rosterEmpty} />

  const mayEdit = may('roster.edit')
  const start = data.dates[0]!.slice(0, 10)

  /* ONE DECISION ABOUT WHICH COLUMN IS TODAY, made here and passed down.

     The header and the cells used to work it out separately from the same field, which
     is two answers to one question — and the first time one of them was changed the
     highlighted heading would have sat above an unhighlighted column with nothing
     failing. `dayKey` is the only place a date is trimmed to a day. */
  const isToday = (iso: string) => dayKey(iso) === dayKey(data.today)

  return (
    <div className="flex flex-col gap-3.5">

      {confirmation && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-hk border border-[#86efac] bg-[#f0fdf4]
                     px-3 py-2 text-[12.5px] text-[#166534]
                     dark:border-[#166534] dark:bg-[#0d2417] dark:text-[#86efac]"
        >
          <CircleCheck className="size-4 shrink-0" />
          <span>{confirmation}</span>
        </div>
      )}

      <Card>
        <CardHeader
          title={t.rosterTitle}
          icon={<CalendarDays className="size-4" />}
          actions={
            <div className="flex items-center gap-1.5">
              {/* CHEVRONS ARE NOT MIRRORED BY HAND. The whole shell is `dir="rtl"` in
                  Arabic and the browser flips the row for us; swapping the icons too
                  would flip it back and send "previous" forward. */}
              <WeekButton
                label={t.rosterPrevWeek}
                icon={<ChevronLeft className="size-3.5" />}
                onClick={() => setFrom(shiftDateKey(start, -7))}
              />
              <button
                type="button"
                onClick={() => setFrom(undefined)}
                className="rounded-hk border border-border px-2.5 py-1 text-xs font-medium
                           hover:bg-muted cursor-pointer"
              >
                {t.rosterThisWeek}
              </button>
              <WeekButton
                label={t.rosterNextWeek}
                icon={<ChevronRight className="size-3.5" />}
                onClick={() => setFrom(shiftDateKey(start, 7))}
              />
            </div>
          }
        />

        {unwritten > 0 && (
          <div className="border-b border-border bg-muted/40 px-3 py-2 text-[12.5px] text-muted-foreground">
            {t.rosterUnwritten.replace('{count}', formatCount(unwritten, locale))}
          </div>
        )}

        {/* The grid scrolls sideways rather than the page: seven columns and a name do
            not fit a narrow window, and a horizontally scrolling PAGE loses the sidebar. */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[46rem] border-collapse text-[12.5px]">
            <thead>
              <tr className="border-b border-border">
                <th className="sticky start-0 z-10 bg-card p-2 text-start font-medium text-muted-foreground">
                  {t.rosterPerson}
                </th>
                {data.dates.map((iso) => (
                  <DayHeader key={iso} iso={iso} isToday={isToday(iso)} locale={locale} t={t} />
                ))}
              </tr>
            </thead>
            <tbody>
              {data.people.map((person) => (
                <tr key={person.userId} className="border-b border-border last:border-0">
                  <PersonCell person={person} locale={locale} t={t} />
                  {data.dates.map((iso) => {
                    /* A CELL THE SERVER DID NOT SEND IS STILL A CELL. The server sends
                       the full grid, so this should never fire — but drawing an empty
                       box for a missing one would make a gap in the DATA look like a
                       decision to leave somebody unrostered, which is the one confusion
                       this screen exists to prevent. Blank it in, and it reads the same
                       as any other cell nobody has written. */
                    const cell = cells.get(cellKey(person.userId, iso))
                      ?? blankCell(person, iso)
                    return (
                      <DayCell
                        key={iso}
                        cell={cell}
                        isToday={isToday(iso)}
                        locale={locale}
                        mayEdit={mayEdit}
                        onEdit={() => { setConfirmation(null); setEditing(cell) }}
                      />
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* A DIALOG, NOT A PANEL BELOW THE GRID.

          It was an inline card until it was rendered: on a 900-pixel window a supervisor
          clicked a cell in the top row and the editor opened seven hundred pixels below
          the fold, so nothing appeared to happen. A dialog cannot be missed, it keeps
          the keyboard in the decision instead of letting it tab back into the grid, and
          — the reason that matters — the stranding warning is the whole point of this
          interaction and must not be somewhere the person never scrolls to. */}
      {editing && (
        <EditCell
          cell={editing}
          person={data.people.find((p) => p.userId === editing.userId)!}
          shifts={data.shifts}
          locale={locale}
          busy={save.isPending}
          error={save.error ? translateError(save.error, locale).message : null}
          onCancel={() => { save.reset(); setEditing(null) }}
          onSave={(state, shiftId, coverage) =>
            save.mutate({
              userId: editing.userId,
              localDate: editing.localDate.slice(0, 10),
              state,
              shiftId,
              coverage,
            })}
        />
      )}
    </div>
  )
}

/** An unwritten cell for a person and a day, when the server sent none. */
function blankCell(person: RosterPerson, localDate: string): RosterCell {
  return {
    userId: person.userId,
    employeeId: person.employeeId,
    displayName: person.displayName,
    displayNameAr: person.displayNameAr,
    roleCode: person.roleCode,
    localDate,
    state: null,
    shiftId: null,
    shiftName: null,
    shiftNameAr: null,
    coverage: null,
    mayReceiveWork: false,
  }
}

function WeekButton(
  { label, icon, onClick }: { label: string; icon: React.ReactNode; onClick: () => void },
) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="rounded-hk border border-border p-1 hover:bg-muted cursor-pointer"
    >
      {icon}
    </button>
  )
}

/** The day a wire date names, whether or not it carries a time. */
function dayKey(iso: string): string {
  return iso.slice(0, 10)
}

function DayHeader(
  { iso, isToday, locale, t }:
  { iso: string; isToday: boolean; locale: 'en' | 'ar'; t: typeof STRINGS['en'] },
) {
  const date = localDateFromKey(iso)
  return (
    <th
      scope="col"
      className={`p-2 text-center font-medium ${
        isToday ? 'bg-primary/5 text-foreground' : 'text-muted-foreground'}`}
    >
      <div>{formatWeekday(date, locale)}</div>
      <div className="text-[11px] font-normal opacity-70">{formatDayMonth(date, locale)}</div>
      {/* THE WORD, not only a tint. A column distinguished by background alone is
          invisible to somebody who cannot see the difference, and this is the column
          every absence gets marked against. */}
      {isToday && <div className="text-[10px] font-semibold text-primary">{t.rosterToday}</div>}
    </th>
  )
}

function PersonCell(
  { person, locale, t }:
  { person: RosterPerson; locale: 'en' | 'ar'; t: typeof STRINGS['en'] },
) {
  return (
    <th
      scope="row"
      className="sticky start-0 z-10 bg-card p-2 text-start font-normal align-top"
    >
      <div className="font-medium">
        <bdi>{nameIn(locale, person.displayName, person.displayNameAr)}</bdi>
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Identifier>{person.employeeId}</Identifier>
        {person.openTaskCount > 0 && (
          <span>{t.rosterHolding.replace('{count}', formatCount(person.openTaskCount, locale))}</span>
        )}
      </div>
    </th>
  )
}

/** ON SHIFT IS THE ONLY STATE WITH A COLOUR. Rest day, leave and absent are all "not
 *  working", and painting three greys three shades apart asks a supervisor to learn a
 *  legend to read a fact the cell already says in words. Absent gets an outline because
 *  it is the one that costs somebody their morning. */
const TONE: Record<RosterState, string> = {
  ON_SHIFT: 'border-[#86efac] bg-[#f0fdf4] text-[#166534] dark:border-[#166534] dark:bg-[#0d2417] dark:text-[#86efac]',
  REST_DAY: 'border-border bg-muted text-muted-foreground',
  LEAVE: 'border-border bg-muted text-muted-foreground',
  ABSENT: 'border-[#fca5a5] bg-[#fef2f2] text-[#b91c1c] dark:border-[#b91c1c] dark:bg-[#2b0f0f] dark:text-[#fca5a5]',
}

function DayCell(
  { cell, isToday, locale, mayEdit, onEdit }:
  {
    cell: RosterCell
    isToday: boolean
    locale: 'en' | 'ar'
    mayEdit: boolean
    onEdit: () => void
  },
) {
  const t = STRINGS[locale]
  const label = cell.state ? t.rosterStates[cell.state] : t.rosterUnsetHint

  const body = (
    <div
      className={`rounded-hk border px-2 py-1 text-center ${
        cell.state
          ? TONE[cell.state]
          /* UNWRITTEN IS DASHED, not grey. A grey box reads as a decision somebody made;
             a dashed outline reads as a gap, which is what it is. */
          : 'border-dashed border-border text-muted-foreground'}`}
    >
      <div className="font-medium">{cell.state ? label : t.rosterUnset}</div>
      {cell.shiftName && (
        <div className="text-[11px] opacity-80">
          <bdi>{nameIn(locale, cell.shiftName, cell.shiftNameAr)}</bdi>
        </div>
      )}
      {cell.coverage && <div className="text-[11px] opacity-70"><bdi>{cell.coverage}</bdi></div>}
    </div>
  )

  return (
    <td className={`p-1.5 align-top ${isToday ? 'bg-primary/5' : ''}`}>
      {mayEdit ? (
        <button
          type="button"
          onClick={onEdit}
          /* THE ACCESSIBLE NAME CARRIES THE STATE, because the visible text alone is
             "Rest day" seven times down a column and a screen reader user would have no
             way to tell which cell they are on. */
          aria-label={`${cell.displayName} — ${label}`}
          className="w-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-hk"
        >
          {body}
        </button>
      ) : body}
    </td>
  )
}

const STATES: RosterState[] = ['ON_SHIFT', 'REST_DAY', 'LEAVE', 'ABSENT']

function EditCell(
  { cell, person, shifts, locale, busy, error, onCancel, onSave }:
  {
    cell: RosterCell
    person: RosterPerson
    shifts: ShiftOption[]
    locale: 'en' | 'ar'
    busy: boolean
    error: string | null
    onCancel: () => void
    onSave: (state: RosterState, shiftId: string | null, coverage: string | null) => void
  },
) {
  const t = STRINGS[locale]
  const [state, setState] = useState<RosterState>(cell.state ?? 'ON_SHIFT')
  const [shiftId, setShiftId] = useState<string | null>(cell.shiftId ?? shifts[0]?.shiftId ?? null)
  const [coverage, setCoverage] = useState(cell.coverage ?? '')

  const onShift = state === 'ON_SHIFT'

  /* THE WARNING THAT MAKES `openTaskCount` WORTH SENDING.

     Work held by somebody who calls in sick STAYS IN THEIR LANE — that is deliberate, so
     a supervisor can see whose morning collapsed rather than watching four rooms vanish
     into a pool. The consequence is that this screen must say so at the moment of the
     decision. Without it the number is a decoration and the rooms are a surprise. */
  const stranding = !onShift && person.openTaskCount > 0

  /* THE NAME AND THE DATE ARE TWO RUNS, NOT ONE STRING.

     They were `'{name} · {date}'` interpolated into a single text node, and the Arabic
     render put the separator INSIDE the date: "زهراء حسين ٥ · أيلول". The mid-dot is a
     neutral character, and a neutral between a word and an Arabic-Indic numeral is
     resolved by its surroundings rather than by what it separates — so the date came
     apart. Two isolated runs cannot do that to each other, which is what <bdi> is for.

     There is no `rosterSet` string any more: a catalogue entry that interpolates two
     differently-directioned runs into one sentence is the defect, not the wording. */

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onCancel() }}>
      <DialogContent aria-describedby={undefined}>
        <div className="flex items-center gap-2 border-b border-border p-3">
          <CalendarDays className="size-4 text-primary" />
          <DialogTitle className="flex items-center gap-1.5 text-[13.5px] font-semibold">
            <Bidi>{nameIn(locale, person.displayName, person.displayNameAr)}</Bidi>
            <span className="text-muted-foreground">·</span>
            <Bidi>{formatDayMonth(localDateFromKey(cell.localDate), locale)}</Bidi>
          </DialogTitle>
        </div>
        <DialogDescription className="sr-only">{t.rosterSubtitle}</DialogDescription>
      <div className="flex flex-col gap-3 p-3">

        <div className="flex flex-wrap gap-1.5">
          {STATES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setState(s)}
              aria-pressed={state === s}
              className={`rounded-hk border px-2.5 py-1 text-xs font-medium cursor-pointer ${
                state === s ? TONE[s] : 'border-border hover:bg-muted'}`}
            >
              {t.rosterStates[s]}
            </button>
          ))}
        </div>

        {/* THE SHIFT PICKER APPEARS ONLY FOR ON-SHIFT, because the server refuses the
            other combination outright: on shift needs a shift, and off shift may not
            have one. Showing a disabled picker beside "Rest day" invites somebody to
            fill it in and then tells them no. */}
        {onShift && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs text-muted-foreground">{t.rosterPickShift}</span>
            <div className="flex flex-wrap gap-1.5">
              {shifts.map((s) => (
                <button
                  key={s.shiftId}
                  type="button"
                  onClick={() => setShiftId(s.shiftId)}
                  aria-pressed={shiftId === s.shiftId}
                  className={`rounded-hk border px-2.5 py-1 text-xs font-medium cursor-pointer ${
                    shiftId === s.shiftId
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border hover:bg-muted'}`}
                >
                  <bdi>{nameIn(locale, s.name, s.nameAr)}</bdi>
                </button>
              ))}
            </div>

            <label className="mt-1 flex flex-col gap-1 text-xs text-muted-foreground">
              {t.rosterCoverage}
              <input
                value={coverage}
                onChange={(e) => setCoverage(e.target.value)}
                placeholder={t.rosterCoveragePlaceholder}
                className="min-w-0 rounded-hk border border-border bg-background px-2 py-1
                           text-[12.5px] text-foreground"
              />
            </label>
          </div>
        )}

        {stranding && (
          <div
            role="status"
            className="flex items-start gap-2 rounded-hk border border-[#fcd34d] bg-[#fffbeb]
                       px-3 py-2 text-[12.5px] text-[#b45309]
                       dark:border-[#b45309] dark:bg-[#2a1e08] dark:text-[#fbbf24]"
          >
            <TriangleAlert className="size-4 shrink-0" />
            <span>
              {t.rosterHoldingWarning
                .replace('{name}', nameIn(locale, person.displayName, person.displayNameAr))
                .replace('{count}', formatCount(person.openTaskCount, locale))}
            </span>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-hk border border-[#fca5a5] bg-[#fef2f2] px-3 py-2 text-[12.5px]
                       text-[#b91c1c] dark:border-[#b91c1c] dark:bg-[#2b0f0f] dark:text-[#fca5a5]"
          >
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            disabled={busy || (onShift && !shiftId)}
            onClick={() => onSave(
              state,
              onShift ? shiftId : null,
              onShift && coverage.trim() ? coverage.trim() : null)}
            className="rounded-hk bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground
                       hover:opacity-90 disabled:opacity-50 cursor-pointer"
          >
            {t.rosterSave}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-hk border border-border px-3 py-1.5 text-xs font-medium
                       hover:bg-muted cursor-pointer"
          >
            {t.rosterCancel}
          </button>
        </div>
      </div>
      </DialogContent>
    </Dialog>
  )
}
